import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CircleTick } from "../../assets/icons/CircleTick";
import { CircleX } from "../../assets/icons/CircleX";
import { jupyterHealthExchangeService } from "../../services/jupyterHealthExchangeService";
import { colors } from "../../styles/colors";
import { preLoginLogo } from "../../styles/preLoginBranding";
import {
  Card,
  IconContainer,
  InfoText,
  Logo,
  MainContainer,
  Message,
  Spinner,
  Title,
} from "../OAuthCallback/OAuthCallbackPage.styles";

type PageState =
  | { kind: "loading"; message: string }
  | { kind: "success"; message: string }
  | { kind: "error"; title: string; message: string; reference?: string };

// One entry per API error code (EN-5228). Only CodeExpired may tell the patient
// to request a new invitation: that advice burns an invitation and a support
// round trip, and for a misconfigured host or a JHE outage it is simply wrong.
const ERROR_MESSAGES: Record<string, { title: string; message: string }> = {
  PatientNotFound: {
    title: "Account Not Found",
    message:
      "No account found for this email. Please contact your care team to set up your account.",
  },
  CodeExpired: {
    title: "Invitation Expired",
    message:
      "This invitation has expired or has already been used. Please request a new one from your care team.",
  },
  InvalidHostname: {
    title: "Invalid Link",
    message: "This invitation link is invalid. Please contact your care team.",
  },
  JheInvitationNotFound: {
    title: "Invitation Not Found",
    message:
      "We couldn't find this invitation in JupyterHealth Exchange. It may have been replaced by a newer one. Please contact your care team.",
  },
  JheInvitationCancelled: {
    title: "Invitation Cancelled",
    message:
      "This invitation was cancelled. Please contact your care team if you still want to connect.",
  },
  JheHostUnreachable: {
    title: "Connection Problem",
    message:
      "We couldn't reach JupyterHealth Exchange at the expected address. This is a problem on our side, not with your invitation. Please contact your care team and quote the reference below.",
  },
  JheUnavailable: {
    title: "Temporarily Unavailable",
    message:
      "JupyterHealth Exchange is temporarily unavailable. Please reopen the link from your invitation email in a few minutes. You do not need a new invitation.",
  },
  JheRejectedRequest: {
    title: "Unexpected Response",
    message:
      "JupyterHealth Exchange rejected the request in a way we didn't expect. Please contact your care team and quote the reference below.",
  },
  JheInvitationGrantInvalid: {
    title: "Unexpected Response",
    message:
      "JupyterHealth Exchange returned an incomplete response. Please contact your care team and quote the reference below.",
  },
  JheTokenExchangeFailed: {
    title: "Sign-in Not Accepted",
    message:
      "JupyterHealth Exchange did not accept the sign-in for this invitation. Please contact your care team and quote the reference below.",
  },
  JheProfileUnavailable: {
    title: "Profile Unavailable",
    message:
      "Your JupyterHealth Exchange profile could not be read. Please contact your care team and quote the reference below.",
  },
  JhePatientDetailUnavailable: {
    title: "Record Unavailable",
    message:
      "Your JupyterHealth Exchange patient record could not be read. Please contact your care team and quote the reference below.",
  },
  JheConsentSubmissionFailed: {
    title: "Consent Not Recorded",
    message:
      "JupyterHealth Exchange did not accept the consent for this invitation. Please contact your care team and quote the reference below.",
  },
};

// Error codes that aren't failures from the patient's perspective — render
// the success card with reassuring copy instead of a red error card.
const INFORMATIONAL_MESSAGES: Record<
  string,
  { title: string; message: string }
> = {
  AlreadyConnected: {
    title: "You're already connected",
    message:
      "Your account is already linked to JupyterHealth Exchange. There's nothing more you need to do — your data is being shared.",
  },
};

function parseInvitationCode(
  code: string
): { hostname: string; invitationToken: string } | null {
  const idx = code.indexOf("_");
  if (idx <= 0 || idx === code.length - 1) return null;
  return {
    hostname: code.slice(0, idx),
    invitationToken: code.slice(idx + 1),
  };
}

interface IErrorBodyLike {
  errors?: { code: string; message: string }[];
  /** Request id echoed by the API (EN-5228); matches the API log lines. */
  correlationId?: string;
}

function extractErrorBody(error: unknown): IErrorBodyLike | null {
  // Service throws the parsed ErrorResponse body directly ({ errors: [...] }).
  // Older flows surfaced it as error.data — accept both for resilience.
  const direct = error as IErrorBodyLike;
  if (Array.isArray(direct?.errors) && direct.errors.length > 0) return direct;
  const nested = (error as { data?: IErrorBodyLike })?.data;
  if (Array.isArray(nested?.errors) && nested.errors.length > 0) return nested;
  return null;
}

function toResultState(error: unknown): PageState {
  const body = extractErrorBody(error);
  const code = body?.errors?.[0]?.code;
  const reference = body?.correlationId || undefined;

  if (code && INFORMATIONAL_MESSAGES[code]) {
    return { kind: "success", message: INFORMATIONAL_MESSAGES[code].message };
  }

  if (code && ERROR_MESSAGES[code]) {
    return { kind: "error", ...ERROR_MESSAGES[code], reference };
  }

  // Unknown / unmapped error code: never surface the raw API or JS exception
  // text to the patient — those are developer messages ("check the logs",
  // stack-trace strings, etc.). Show a single curated fallback instead.
  return {
    kind: "error",
    title: "Something went wrong",
    message:
      "We couldn't complete your JupyterHealth Exchange link right now. Please try the link again or contact your care team if the problem continues.",
    reference,
  };
}

function getStatusBucket(
  kind: PageState["kind"]
): "loading" | "success" | "error" {
  if (kind === "loading") return "loading";
  if (kind === "success") return "success";
  return "error";
}

function getResultTitle(state: PageState): string {
  if (state.kind === "success") return "Account linked!";
  if (state.kind === "error") return state.title;
  return "Linking your account...";
}

function getResultMessage(state: PageState): string {
  if (state.kind === "loading") return state.message;
  if (state.kind === "success") return state.message;
  return state.message;
}

export function JupyterHealthExchangeCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({
    kind: "loading",
    message: "Linking your JupyterHealth Exchange account...",
  });
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = searchParams.get("code");
    if (!code) {
      setState({
        kind: "error",
        title: "Invalid Link",
        message:
          "Missing invitation code. Please use the link from your invitation email.",
      });
      return;
    }

    const parsed = parseInvitationCode(code);
    if (!parsed) {
      setState({
        kind: "error",
        title: "Invalid Link",
        message:
          "The invitation code is malformed. Please use the original link from your invitation email.",
      });
      return;
    }

    navigate({ pathname: "/jhe/callback", search: "" }, { replace: true });

    jupyterHealthExchangeService
      .completeInvitation(parsed)
      .then((response) => {
        if (response.success) {
          setState({
            kind: "success",
            message:
              response.message ||
              "Your JupyterHealth Exchange account has been linked.",
          });
        } else {
          setState({
            kind: "error",
            title: "Connection Failed",
            message: response.message || "Failed to link your account.",
          });
        }
      })
      .catch((error) => setState(toResultState(error)));
  }, [navigate, searchParams]);

  const status = getStatusBucket(state.kind);
  const title = getResultTitle(state);
  const message = getResultMessage(state);
  const reference = state.kind === "error" ? state.reference : undefined;

  let icon: React.ReactNode;
  if (status === "loading") {
    icon = <Spinner />;
  } else if (status === "success") {
    icon = <CircleTick width={44} height={44} color={colors.success} />;
  } else {
    icon = <CircleX width={44} height={44} color={colors.error} />;
  }

  return (
    <MainContainer>
      <Card>
        <Logo src={preLoginLogo.src} alt={preLoginLogo.alt} />
        <IconContainer status={status}>{icon}</IconContainer>
        <Title>{title}</Title>
        <Message>{message}</Message>
        {status !== "loading" ? (
          <InfoText>
            {status === "success"
              ? "You can close this window."
              : "If you continue to experience issues, please contact your care team."}
          </InfoText>
        ) : null}
        {reference ? (
          // EN-5228: the API's correlation id. Support can match a screenshot of
          // this card to the exact log lines for the request that failed.
          <InfoText data-testid="jhe-error-reference">
            Reference: {reference}
          </InfoText>
        ) : null}
      </Card>
    </MainContainer>
  );
}
